import React from "react";
import { reportClientError } from "../utils/clientErrorReporter";

/**
 * AppErrorBoundary — sacred fallback UI when something throws.
 *
 * Reports the crash to /api/client-errors and shows a calm, on-brand
 * recovery screen instead of a blank white page. Lives at the root of the
 * app tree so any thrown error gets caught here.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message };
  }

  componentDidCatch(error, info) {
    reportClientError({
      message: error?.message || "React boundary error",
      stack: error?.stack,
      componentStack: info?.componentStack,
      severity: "error",
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, errorMessage: undefined });
    if (typeof window !== "undefined") {
      window.location.href = "/dashboard";
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 p-6"
          data-testid="app-error-boundary"
        >
          <div className="max-w-md w-full text-center">
            <div className="text-6xl mb-4" aria-hidden="true">
              🌿
            </div>
            <h1 className="text-xl font-semibold text-foreground mb-2">
              Something rippled
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              The page hit a snag, but your data is safe.
              Take a breath and we'll bring you back to your dashboard.
            </p>
            <button
              onClick={this.handleReset}
              className="rounded-full bg-gradient-to-r from-emerald-500 to-amber-500 hover:from-emerald-600 hover:to-amber-600 text-white text-sm font-medium px-6 py-2.5 transition-all shadow-md hover:shadow-lg"
              data-testid="error-boundary-reset"
            >
              Return home →
            </button>
            {process.env.NODE_ENV === "development" && this.state.errorMessage && (
              <p className="mt-4 text-[11px] text-muted-foreground/70 font-mono break-all">
                {this.state.errorMessage}
              </p>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
